import { TBillingStatus } from 'types/Billing';
import { ThunkApiType } from 'types/utils';
import authFetch, { checkForUnauthorizedResponse } from 'utils/axios';
import { BILLING_STATUS } from 'utils/constants';
import { exportListToExcel } from 'utils/download';
import {
	buildBillingFilterParams,
	buildMembersBillingFilterParams,
} from 'utils/utils';
import { resetInputFilters } from '../form/formSlice';
import {
	getClaimsBilling,
	getMembersBilling,
	selectIsMembers,
} from './billingSlice';

const getBillingPath = (tabSelected: string, isMembers: boolean) => {
	const segment = isMembers ? 'members' : 'claims';
	return tabSelected === 'admin' ? `admin/${segment}` : segment;
};

export const getClaimsBillingThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	const { pages, tabSelected } = thunkAPI.getState().billing;
	const page = pages.claims;
	const billingsSearchValues = thunkAPI.getState().form;
	const { sortingFields, sortingDirections } = thunkAPI.getState().form;

	const path = tabSelected === 'admin' ? 'admin/claims' : 'claims';
	const claimsBillingURL = `/billings/${path}?page=${page}&size=10`;

	let sortParams = '';

	if (sortingFields && sortingDirections) {
		sortParams = `&sortingFields=${sortingFields}&sortingDirections=${sortingDirections}`;
	}

	const { queryParams: searchParams } =
		buildBillingFilterParams(billingsSearchValues);

	const getClaimsBillingURL = `${claimsBillingURL}${sortParams}${searchParams}`;

	try {
		const response = await authFetch.get(getClaimsBillingURL);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getClaimsBillingStatusesThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	const getClaimsBillingStatusesURL = '/billings/claims/statuses';

	try {
		const response = await authFetch.get(getClaimsBillingStatusesURL);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getAdminBillingStatusesThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	const getAdminBillingStatusesURL = '/billings/admin/claims/statuses';

	try {
		const response = await authFetch.get(getAdminBillingStatusesURL);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getBillingTypologiesThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	const { tabSelected } = thunkAPI.getState().billing;
	const getBillingTypologiesURL = `/billings/${tabSelected}/typologies`;

	try {
		const response = await authFetch.get(getBillingTypologiesURL);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const changeBillingStatusThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	const { tabSelected } = thunkAPI.getState().billing;
	const { selectedItems, downloadAll } = thunkAPI.getState().tableSelectedItems;
	const formState = thunkAPI.getState().form;

	const isMembers = selectIsMembers(thunkAPI.getState());

	const { body: filterBody } = isMembers
		? buildMembersBillingFilterParams(formState)
		: buildBillingFilterParams(formState);

	const path = getBillingPath(tabSelected, isMembers);
	const changeBillingStatusURL = `/billings/${path}/status`;

	const body = {
		...filterBody,
		...(isMembers
			? downloadAll
				? { memberExcludeIds: selectedItems.length > 0 ? selectedItems : null }
				: { memberIncludeIds: selectedItems.length > 0 ? selectedItems : null }
			: downloadAll
				? { claimExcludeUuids: selectedItems.length > 0 ? selectedItems : null }
				: { claimIncludeUuids: selectedItems.length > 0 ? selectedItems : null }),
	};

	try {
		const response = await authFetch.put(changeBillingStatusURL, body);
		if (isMembers) {
			thunkAPI.dispatch(resetInputFilters());
			thunkAPI.dispatch(getMembersBilling(''));
		} else {
			thunkAPI.dispatch(getClaimsBilling(''));
		}
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getCSVBillingExportThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	const { tabSelected } = thunkAPI.getState().billing;
	const { inputBillingStatus, inputMemberBillingStatus } =
		thunkAPI.getState().form;
	const formState = thunkAPI.getState().form;

	const { selectedItems, downloadAll } = thunkAPI.getState().tableSelectedItems;

	const isMembers = selectIsMembers(thunkAPI.getState());

	const { body: filterBody } = isMembers
		? buildMembersBillingFilterParams(formState)
		: buildBillingFilterParams(formState);

	const path = getBillingPath(tabSelected, isMembers);
	const csvBillingExportURL = `/billings/${path}/csv`;

	const body = {
		...filterBody,
		...(isMembers
			? downloadAll
				? { memberExcludeIds: selectedItems.length > 0 ? selectedItems : null }
				: { memberIncludeIds: selectedItems.length > 0 ? selectedItems : null }
			: downloadAll
				? { claimExcludeUuids: selectedItems.length > 0 ? selectedItems : null }
				: { claimIncludeUuids: selectedItems.length > 0 ? selectedItems : null }),
	};

	try {
		let fileName = '';

		const response = await authFetch.post(csvBillingExportURL, body, {
			responseType: 'blob',
		});

		switch (true) {
			case isMembers:
				fileName = `MembersBilling_${BILLING_STATUS[inputMemberBillingStatus as TBillingStatus]}`;
				break;
			case tabSelected === 'admin':
				fileName = `AdminFeeBilling_${BILLING_STATUS[inputBillingStatus as TBillingStatus]}`;
				break;
			default:
				fileName = `ClaimsBilling_${BILLING_STATUS[inputBillingStatus as TBillingStatus]}`;
				break;
		}

		exportListToExcel(response.data, `${fileName}`, 'csv');
		return true;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const rebillClaimThunk = async (
	uuid: string,
	thunkAPI: ThunkApiType,
) => {
	const rebillClaimURL = `/claim/${uuid}/rebill`;

	try {
		const response = await authFetch.post(rebillClaimURL, {});
		thunkAPI.dispatch(getClaimsBilling(''));
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getMembersBillingThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	const page = thunkAPI.getState().billing.pages.members;
	const formState = thunkAPI.getState().form;
	const { sortingFields, sortingDirections } = formState;

	const membersBillingURL = `/billings/admin/member?page=${page}&size=10`;

	let sortParams = '';
	if (sortingFields && sortingDirections) {
		sortParams = `&sortingFields=${sortingFields}&sortingDirections=${sortingDirections}`;
	}

	const { queryParams: searchParams } =
		buildMembersBillingFilterParams(formState);

	try {
		const response = await authFetch.get(
			`${membersBillingURL}${sortParams}${searchParams}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getMembersAggregationBillingThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	const page = thunkAPI.getState().billing.pages.aggregation;
	const formState = thunkAPI.getState().form;
	const { sortingFields, sortingDirections } = formState;

	const membersBillingURL = `/billings/admin/member/aggregation?page=${page}&size=10`;

	let sortParams = '';
	if (sortingFields && sortingDirections) {
		sortParams = `&sortingFields=${sortingFields}&sortingDirections=${sortingDirections}`;
	}

	const { queryParams: searchParams } = buildMembersBillingFilterParams(
		formState,
		true,
	);

	try {
		const response = await authFetch.get(
			`${membersBillingURL}${sortParams}${searchParams}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const downloadMembersAggregationBillingThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	const formState = thunkAPI.getState().form;
	const { selectedItems, downloadAll } = thunkAPI.getState().tableSelectedItems;
	const { sortingFields, sortingDirections } = formState;

	let sortParams = '';
	if (sortingFields && sortingDirections) {
		sortParams = `?sortingFields=${sortingFields}&sortingDirections=${sortingDirections}`;
	}

	const { queryParams: searchParams } = buildMembersBillingFilterParams(
		formState,
		true,
	);

	const body = {
		...(downloadAll
			? { aggrExcludeids: selectedItems.length > 0 ? selectedItems : null }
			: { aggrIncludeUuids: selectedItems.length > 0 ? selectedItems : null }),
	};

	try {
		const response = await authFetch.post(
			`/billings/admin/member/aggregation/pdf${sortParams}${searchParams}`,
			body,
			{
				responseType: 'blob',
			},
		);
		exportListToExcel(response.data, `billing_members_aggregated`, 'pdf');
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

// For the moment we only have 2 relationship types
// maybe in the future we can replace this with a real API call
export const getRelationshipTypesThunk = async () => {
	return [
		{ code: 'S', name: 'Subscriber' },
		{ code: 'D', name: 'Dependant' },
	];
};

export const getPlanCodesThunk = async (_: string, thunkAPI: ThunkApiType) => {
	const getGroupsURL = '/plans/domain';

	try {
		const response = await authFetch.get(getGroupsURL);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};
